import {
  APIFieldType,
  FieldGlobalAttributes,
  FormattingOperator,
} from '@ardoq/api-types';
import { uniqBy } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import {
  booleanComparators,
  dateComparators,
  dateRangeComparators,
  selectComparators,
  standardComparators,
} from './filteringComparators';
import {
  composePerspectiveFilterFromField,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { Formatting } from '../types';
import {
  dateRangeOperations,
  getStartDateName,
  getEndDateName,
} from '@ardoq/date-range';
import { getAggregatedDateRangeByStartFieldName } from './dateRangeUtils';
import { CUSTOM_FIELDS_FORMATTING_GROUP_LABEL } from '../../../consts';
import { Locale, localeCompare } from '@ardoq/locale';

const getComparatorForFieldType = (
  fieldType: APIFieldType
): FormattingOperator[] => {
  switch (fieldType) {
    case APIFieldType.CHECKBOX:
      return booleanComparators;
    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_TIME:
      return dateComparators;
    case APIFieldType.SELECT_MULTIPLE_LIST:
    case APIFieldType.LIST:
      return selectComparators;
    default:
      return standardComparators;
  }
};

const dateRangeFieldToFormatting = (
  dateRangeField: FieldGlobalAttributes
): Formatting | null => {
  // we need one generated formatting for each pair of *_start_date and *_end_date, so we generate it only for the *_start_date case
  const aggregatedName = getAggregatedDateRangeByStartFieldName(
    dateRangeField.name
  );

  if (!aggregatedName) {
    return null;
  }

  return {
    id: aggregatedName,
    label: dateRangeOperations.extractDateRangeFieldLabel(dateRangeField.label),
    filterType: QueryBuilderFilterType.DATE_RANGE,
    selectOptions: undefined,
    comparators: dateRangeComparators,
  };
};

const toCustomFieldFormatting = (
  field: FieldGlobalAttributes,
  isNewConditionalFormatting: boolean
): Formatting => {
  const { id, label, filterType, selectOptions, numberFormatOptions } =
    composePerspectiveFilterFromField(field, isNewConditionalFormatting);

  return {
    id,
    label,
    filterType,
    selectOptions,
    numberFormatOptions,
    comparators: getComparatorForFieldType(field.type),
  };
};

// Returns a comparator that sorts date range fields so that related entries
// (aggregate, start, end) are grouped together and ordered as:
// aggregate (0), start (1), end (2). All other fields sort alphabetically.
const getFilterComparator = (
  dateRangeFormattings: Formatting[],
  locale: Locale
) => {
  const fieldIdToAggregateLabel = new Map<string, string>();
  const fieldIdToGroupPosition = new Map<string, number>();
  dateRangeFormattings.forEach(aggregate => {
    const startName = getStartDateName(aggregate.id);
    const endName = getEndDateName(aggregate.id);
    fieldIdToAggregateLabel.set(startName, aggregate.label);
    fieldIdToAggregateLabel.set(endName, aggregate.label);
    fieldIdToGroupPosition.set(aggregate.id, 0);
    fieldIdToGroupPosition.set(startName, 1);
    fieldIdToGroupPosition.set(endName, 2);
  });

  return (a: Formatting, b: Formatting): number => {
    const sortKeyA = fieldIdToAggregateLabel.get(a.id) ?? a.label;
    const sortKeyB = fieldIdToAggregateLabel.get(b.id) ?? b.label;

    const groupCompare = localeCompare(sortKeyA, sortKeyB, locale);
    if (groupCompare !== 0) return groupCompare;

    const posA = fieldIdToGroupPosition.get(a.id);
    const posB = fieldIdToGroupPosition.get(b.id);
    if (posA !== undefined && posB !== undefined) return posA - posB;

    return localeCompare(a.label, b.label, locale);
  };
};

export const getCustomFieldsFormattingGroupIfAnyFieldIsAvailableOrNull = (
  relatedFields: FieldGlobalAttributes[],
  isNewConditionalFormatting: boolean,
  locale: Locale
) => {
  const uniqueRelatedFields = uniqBy(relatedFields, 'name');
  if (uniqueRelatedFields.length === 0) {
    return null;
  }

  const customFieldFormattings = uniqueRelatedFields
    .map(field => toCustomFieldFormatting(field, isNewConditionalFormatting))
    .filter(isTruthy);

  /**
   For each date range field (e.g. Active Period), there's going to be three available options:
   Active Period - Start (with date comparators)
   Active Period - End (with date comparators)
   Active Period (with date range comparators, generated separately, with id "active_period_start_date,active_period_end_date")
   */
  const dateRangeFormattingForBothStartAndEnd = uniqueRelatedFields
    .map(field => dateRangeFieldToFormatting(field))
    .filter(isTruthy);

  return {
    label: CUSTOM_FIELDS_FORMATTING_GROUP_LABEL,
    filters: [
      ...customFieldFormattings,
      ...dateRangeFormattingForBothStartAndEnd,
    ].sort(getFilterComparator(dateRangeFormattingForBothStartAndEnd, locale)),
  };
};
